import React from 'react';
import SVG from 'react-inlinesvg';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { __ } from '@wordpress/i18n';
import PropTypesConfig from 'config/prop-types';
import displayNumber from 'util/displayNumber';
import displayText from 'util/displayText';
import formatDate from 'util/formatDate';
import timeAgoDate from 'util/timeAgo';
import formatNumber from 'util/formatNumber';
import { ThemeProvider } from 'styled-components';

// Shared styles.
import cardStyles from 'styles/cards';
import helperStyles from 'styles/helpers';
import tableStyles from 'styles/table';
import theme from 'styles/theme';
import CheckIcon from 'styles/checkmark.svg';

// Styled components.
import styles from './styles';

/**
 * A component representing a results card for a ballot measure.
 */
export default class MeasureCard extends React.PureComponent {
  /**
   * Set PropTypes for this component.
   * @type {object}
   */
  static propTypes = PropTypesConfig.MEASURE_SHAPE;

  /**
   * Set Initial State
   * @type {object}
   */
  state = {
    updatedDate: '',
  };

  /**
   * On initial mount set interval.
   */
  componentDidMount() {
    // Display updated Time on init.
    this.updatedTimer();

    // Set interval to fire every 60 seconds.
    this.updatedId = setInterval(this.updatedTimer.bind(this), 60 * 1000);
  }

  /**
   * On unmount, clear interval.
   */
  componentWillUnmount() {
    clearInterval(this.updatedId);
  }

  /**
   * Update Last Updated date.
   */
  updatedTimer() {
    const {
      last_updated: lastUpdated,
    } = this.props;

    this.setState({
      updatedDate: timeAgoDate(formatDate(lastUpdated)),
    });
  }

  /**
   * Render this component.
   * @returns {object} - JSX for this component.
   */
  render() {
    // Shared Styled Components.
    const {
      Card,
      Description,
      GlobalStyles,
      HeaderWrap,
      ItemStatus,
      ItemTitle,
      SubTitle,
      Title,
    } = cardStyles;
    const {
      Table,
      TableCell,
      TableFooter,
      TableHeader,
      TableRow,
    } = tableStyles;
    const {
      ScreenReaderText,
    } = helperStyles;

    // Measure Styled Components.
    const {
      MeasureInfoWrap,
    } = styles;

    const {
      description,
      no_percentage: noPercentage,
      no_votes: noVotes,
      passed,
      precincts_percentage: precinctsPercentage,
      subtitle,
      suppress_updated: suppressUpdated = false,
      title,
      yes_percentage: yesPercentage,
      yes_votes: yesVotes,
    } = this.props;

    const {
      updatedDate,
    } = this.state;

    const display = {
      description: displayText(description),
      lastUpdated: ! suppressUpdated,
      percentage: displayNumber(yesPercentage) && displayNumber(noPercentage),
      reporting: displayNumber(precinctsPercentage),
      subtitle: displayText(subtitle),
      title: displayText(title),
      total: displayNumber(yesVotes) && displayNumber(noVotes),
    };

    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Card
          className={
            classNames(
              'election-hub__card',
              'election-hub--measure-card__candidates',
            )
          }
        >
          <Table>
            {(display.title || display.subtitle || display.description) && (
              <caption>
                {display.title && (
                  <Title className="election-hub__card--title">
                    {title}
                  </Title>
                )}
                {display.subtitle && (
                  <SubTitle
                    className="election-hub__card--sub-title"
                  >
                    {subtitle}
                  </SubTitle>
                )}
                {display.description && (
                  <Description className="election-hub__card--description">
                    <span
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ // phpcs:disable WordPressVIPMinimum.JS.DangerouslySetInnerHTML.Found
                        __html: DOMPurify.sanitize(description),
                      }}
                    />
                  </Description>
                )}
              </caption>
            )}
            <thead>
              <tr className="election-hub__info-header">
                <TableHeader scope="col">
                  <ScreenReaderText>
                    {__('Measure', 'election-hub')}
                  </ScreenReaderText>
                </TableHeader>
                {display.total && (
                  <TableHeader scope="col">
                    {__('Votes', 'election-hub')}
                  </TableHeader>
                )}
                {display.percentage && (
                  <TableHeader scope="col">
                    {__('Percent', 'election-hub')}
                  </TableHeader>
                )}
              </tr>
            </thead>
            <tbody>
              <TableRow className={
                classNames(
                  'measure__yes',
                  'measure__winner',
                  {
                    'measure__yes--winner': 'yes' === passed,
                  }
                )
              }
              >
                <TableHeader scope="row">
                  <HeaderWrap>
                    <MeasureInfoWrap>
                      <ItemTitle>
                        {__('Yes', 'election-hub')}
                      </ItemTitle>
                      {'yes' === passed && (
                        <ItemStatus type="measure">
                          <ScreenReaderText>
                            {__('Measure Passed', 'election-hub')}
                          </ScreenReaderText>
                          <SVG src={CheckIcon} />
                        </ItemStatus>
                      )}
                    </MeasureInfoWrap>
                  </HeaderWrap>
                </TableHeader>
                {display.total && (
                  <TableCell>{formatNumber(yesVotes)}</TableCell>
                )}
                {display.percentage && (
                  <TableCell
                    className="election-hub__percentage"
                  >
                    {`${formatNumber(yesPercentage, 1)}%`}
                  </TableCell>
                )}
              </TableRow>
              <TableRow className={
                classNames(
                  'measure__no',
                  'measure__winner',
                  {
                    'measure__no--winner': 'no' === passed,
                  }
                )
              }
              >
                <TableHeader scope="row">
                  <HeaderWrap>
                    <MeasureInfoWrap>
                      <ItemTitle>
                        {__('No', 'election-hub')}
                      </ItemTitle>
                      {'no' === passed && (
                        <ItemStatus>
                          <ScreenReaderText>
                            {__('Measure Failed', 'election-hub')}
                          </ScreenReaderText>
                          <SVG src={CheckIcon} />
                          {__('Failed', 'election-hub')}
                        </ItemStatus>
                      )}
                    </MeasureInfoWrap>
                  </HeaderWrap>
                </TableHeader>
                {display.total && (
                  <TableCell>{formatNumber(noVotes)}</TableCell>
                )}
                {display.percentage && (
                  <TableCell
                    className="election-hub__percentage"
                  >
                    {`${formatNumber(noPercentage, 1)}%`}
                  </TableCell>
                )}
              </TableRow>
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5">
                  <TableFooter>
                    {display.lastUpdated && updatedDate && (
                      <div className="election-hub--last-updated">
                        {__('Updated: ', 'election-hub')}
                        {updatedDate}
                      </div>
                    )}
                    {display.reporting && (
                      <div className="election-hub--reporting">
                        {`${precinctsPercentage}% `}
                        {__('reporting', 'election-hub')}
                      </div>
                    )}
                  </TableFooter>
                </td>
              </tr>
            </tfoot>
          </Table>
        </Card>
      </ThemeProvider>
    );
  }
}
